import { useScheduleContext } from '@/pages/Schedule/contexts';
import type { LabelType } from '@/types/gql.generated';
import { definitions as labelDefinitions } from '../../utils/labelDefinitions';
import { useCreateLabel } from './hooks/useCreateLabel';
import { useDeleteLabel } from './hooks/useDeleteLabel';
import { useUpdateLabel } from './hooks/useUpdateLabel';
import { useUpdateLabelSortOrder } from './hooks/useUpdateLabelSortOrder';
import { LabelDataContext, type LabelData } from './LabelDataContext';

type Props = {
  labelType: LabelType;
  children: React.ReactNode;
};

/**
 * Feeds the shared LabelDataContext with Schedule-backed CRUD operations.
 * Wrap any LabelSelect usage inside a Schedule page with this provider so
 * Schedule behaviour (cache updates, filter removal, etc.) is preserved.
 */
export const ScheduleLabelDataProvider = ({ labelType, children }: Props) => {
  const { schedule, scheduleId } = useScheduleContext();

  const catalogLabels = schedule
    ? labelDefinitions[labelType].scheduleGetter(schedule)
    : [];

  const { createLabel, isPending } = useCreateLabel(labelType);
  const { updateLabel } = useUpdateLabel();
  const { deleteLabel } = useDeleteLabel(labelType);
  const { mutate: sortOrderMutate } = useUpdateLabelSortOrder(
    labelType,
    catalogLabels
  );

  const value: LabelData = {
    labelType,
    labels: catalogLabels,
    createLabel,
    isCreatePending: isPending,
    updateLabel,
    removeLabel: deleteLabel,
    updateSortOrder: (labels) => {
      sortOrderMutate({
        scheduleId,
        labels: labels.map(({ id }, index) => ({ id, sortOrder: index })),
      });
    },
  };

  return (
    <LabelDataContext.Provider value={value}>
      {children}
    </LabelDataContext.Provider>
  );
};
